import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Upload, BarChart3, ChevronRight, Image, FileAudio, Video, Sparkles, ShieldCheck, Activity, AlertTriangle, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { StatCard } from '../components/Dashboard/StatCard';
import { ScanCard } from '../components/Dashboard/ScanCard';
import { ProgressOverviewSection } from '../components/Dashboard/ProgressOverviewSection';
import { CommunicationStyleWidget } from '../components/Dashboard/CommunicationStyleWidget';
import { WeeklyInsightCard } from '../components/Dashboard/WeeklyInsightCard';
import { ReferralBanner } from '../components/Referral/ReferralBanner';
import { useAuth } from '../contexts/AuthContext';
import { useUpgradeModal } from '../contexts/UpgradeModalContext';
import { consumeNoCreditsPrompt, consumeWelcomeModalPending } from '../utils/upgradePrompts';
import { useScans } from '../hooks/useScans';
import { useWeeklyInsights } from '../hooks/useWeeklyInsights';
import { supabase } from '../lib/supabaseClient';
import { trackCriticalEvent } from '../utils/mixpanelTracking';
export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const {
    scans,
    loading
  } = useScans(user?.id);
  const [dashboardText, setDashboardText] = useState('');
  const {
    insights
  } = useWeeklyInsights(user?.id);
  const {
    openUpgradeModal
  } = useUpgradeModal();
  const [profile, setProfile] = useState<{
    is_pro: boolean;
    scan_credits: number;
  } | null>(null);
  const [loadingProStatus, setLoadingProStatus] = useState(true);
  const firstName = user?.user_metadata?.firstName || 'User';
  const isPro = profile?.is_pro || false;
  const scansCount = profile?.scan_credits || 0;
  // Determine user tier: 'pro' | 'active' (has credits) | 'free' (no credits)
  const userTier = isPro ? 'pro' : scansCount > 0 ? 'active' : 'free';
  // Calculate stats from real scans - MOVED BEFORE useEffects that reference them
  const totalScans = scans.length;
  const completedScans = scans.filter(s => s.status === 'completed');
  const toxicScans = completedScans.filter(s => s.risk_level === 'high' || s.risk_level === 'medium');
  const avgScore = completedScans.length > 0 ? Math.round(completedScans.reduce((sum, s) => sum + (s.score || 0), 0) / completedScans.length) : null;
  // Get recent scans (top 3)
  const recentScans = scans.slice(0, 3);
  // Determine pricing display
  const pricingDisplay = isPro ? 'Unlimited Active' : scansCount > 0 ? 'Scans available' : 'Single scan — $2.99';
  // Fetch profile data from profiles table
  useEffect(() => {
    async function fetchProfile() {
      if (!user?.id) return;
      try {
        const {
          data,
          error
        } = await supabase.from('profiles').select('is_pro, scan_credits').eq('id', user.id).single();
        if (error) throw error;
        setProfile(data);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setProfile(null);
      } finally {
        setLoadingProStatus(false);
      }
    }
    fetchProfile();
    // Real-time subscription for profile updates
    const channel = supabase.channel('profile-dashboard-updates').on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'profiles',
      filter: `id=eq.${user?.id}`
    }, payload => {
      const newProfile = payload.new as {
        is_pro: boolean;
        scan_credits: number;
      };
      setProfile(newProfile);
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);
  // Prompts, in priority order: greet a brand-new account once, otherwise
  // surface the out-of-credits options at most once per session.
  useEffect(() => {
    if (loadingProStatus || !user?.id) return;
    if (consumeWelcomeModalPending()) {
      openUpgradeModal('welcome');
      return;
    }
    if (!isPro && scansCount === 0 && consumeNoCreditsPrompt()) {
      openUpgradeModal('no_credits');
    }
  }, [loadingProStatus, user?.id, isPro, scansCount, openUpgradeModal]);
  // Track dashboard view on mount
  useEffect(() => {
    if (!loading && !loadingProStatus && user?.id) {
      trackCriticalEvent('Dashboard_Viewed', {
        total_scans: totalScans,
        completed_scans: completedScans.length,
        is_pro: isPro,
        scans_remaining: scansCount,
        avg_toxicity_score: avgScore
      });
    }
  }, [loading, loadingProStatus, user?.id, totalScans, completedScans.length, isPro, scansCount, avgScore]);
  const fileTypes = [{
    icon: FileText,
    label: 'Text/PDF',
    color: 'text-gray-400'
  }, {
    icon: Image,
    label: 'Screenshots',
    color: 'text-gray-400'
  }, {
    icon: Video,
    label: 'Videos',
    color: 'text-gray-400'
  }, {
    icon: FileAudio,
    label: 'Audio',
    color: 'text-gray-400'
  }];
  return <div className="bg-[#050505] min-h-screen text-white" data-id="element-3650" data-mp="1zzxiym9iusk6">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none fixed" data-id="element-3651" data-mp="2wpog6t976tto" />

      <WebsiteHeader data-id="element-3652" data-mp="2iq5k4ytnv8xq" />

      <div className="max-w-6xl mx-auto w-full relative z-10" data-id="element-3653" data-mp="2iqyipcigus4s">
        <main className="px-4 md:px-6 pt-8 md:pt-12 pb-24 md:pb-16" data-id="element-3654" data-mp="2isjboxp4u30j">
          {/* Welcome Section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="mb-10" data-id="element-3655" data-mp="17b04ku8flfwp">
            <div className="flex items-center gap-2 mb-4" data-id="element-3656" data-mp="zrhek40rsmem">
              <span className="w-2 h-2 bg-[#FF0000] rounded-full animate-pulse" data-id="element-3657" data-mp="zqz86eke8rsz" />
              <span className="text-[#FF0000] font-mono text-xs tracking-widest uppercase font-bold" data-id="element-3658" data-mp="14p962wxi66ia">
                Operative Terminal Active
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-3 uppercase tracking-tight" data-id="element-3659" data-mp="usnzw89qypcv">
              Operative: {firstName}
            </h2>
            <p className="text-gray-400 font-mono text-sm md:text-base max-w-2xl" data-id="element-3660" data-mp="gz6gqokz3end">
              Forensic surveillance dashboard — monitor threat patterns and
              behavioral anomalies across intercepted communications.
            </p>
          </motion.div>

          {/* Upload Card - Forensic Style */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="relative bg-[#0a0a0a] border-2 border-gray-800 p-8 md:p-10 mb-10 group-hover:border-[#FF0000]/50 transition-all duration-300 group" data-id="element-3661" data-mp="3oliuf7nzpyml">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-[#FF0000]" data-id="element-3662" data-mp="bcmi0e1et1m6" />
            <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-[#FF0000]" data-id="element-3663" data-mp="6e1mgrx3r642" />
            <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-[#FF0000]" data-id="element-3664" data-mp="g5pbkm66sb2d" />
            <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-[#FF0000]" data-id="element-3665" data-mp="u5glaapo75ak" />

            {/* Scanning Line */}
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FF0000] to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-[scan_2s_ease-in-out_infinite]" data-id="element-3666" data-mp="q4wml2pg7tmb" />

            <div className="relative z-20" data-id="element-3667" data-mp="21gcpkmv14pr4">
              <div className="flex items-center gap-3 mb-6" data-id="element-3668" data-mp="26h0pjy18kgxh">
                <div className="inline-flex items-center justify-center w-10 h-10 bg-[#FF0000]/10 border border-[#FF0000]" data-id="element-3669" data-mp="26hsbsi8y12b3">
                  <FileText className="w-5 h-5 text-[#FF0000]" data-id="element-3670" data-mp="2ag9i4zkji8rg" />
                </div>
                <h3 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight" data-id="element-3671" data-mp="1dq8pgph1v84v">
                  Initialize Forensic Scan
                </h3>
              </div>

              <textarea value={dashboardText} onChange={e => setDashboardText(e.target.value)} className="w-full h-32 bg-[#050505] border border-gray-800 focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] text-white p-4 font-mono text-sm resize-none transition-colors" placeholder="Paste your chat conversation here (e.g., texts, emails, or messages) to begin your forensic analysis..." data-id="element-3672" data-mp="1mrqcyswsec3l" />

              <div className="flex flex-col md:flex-row items-start md:items-center justify-between mt-4 gap-4" data-id="element-3673" data-mp="mlwds3nge9d3">
                <p className="text-[11px] text-gray-500 font-mono max-w-md" data-id="element-3674" data-mp="ihokdt61w58j">
                  Fast & Simple: Just copy the messages from your chat app and
                  paste them right here.
                </p>
                <button disabled={!dashboardText.trim()} onClick={() => navigate('/upload', {
                state: {
                  initialText: dashboardText
                }
              })} className="px-8 py-3 bg-[#FF0000] text-white font-bold uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-600 transition-colors whitespace-nowrap shadow-[0_0_15px_rgba(255,0,0,0.2)]" data-id="element-3675" data-mp="8n51mms5ixrl">
                  Analyze Text
                </button>
              </div>
            </div>
          </motion.div>

          {/* Stats Grid - Forensic Style */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-id="element-3676" data-mp="2s4ost0es1jxk">
            <StatCard value={totalScans.toString()} label="Cases Analyzed" icon={<FileText size={20} data-id="element-3678" data-mp="2n8q2365z7ptz" />} data-id="element-3677" data-mp="29987ub9wvkd8" />
            <StatCard value={toxicScans.length.toString()} label="Threat Detections" icon={<ShieldCheck size={20} data-id="element-3680" data-mp="2g4letqb0v7k" />} data-id="element-3679" data-mp="2i7sh6ltl0hos" />
            <StatCard value={avgScore !== null ? avgScore.toString() : '—'} label="Avg. Threat Index" icon={<Activity size={20} data-id="element-3682" data-mp="1feof9unj476d" />} description="Threat Scale: 0–100" data-id="element-3681" data-mp="3tnh8tqhs64vx" />
          </motion.div>

          {/* Weekly Insight */}
          {insights?.primaryInsight && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.25
        }} className="mb-10" data-id="element-3683" data-mp="16ft567hd6cxs">
              <WeeklyInsightCard insight={insights.primaryInsight} data-id="element-3684" data-mp="1pdx5qdj6r3nz" />
            </motion.div>}

          {/* Progress Overview Section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} data-id="element-3685" data-mp="1ubp8y3pa6c7h">
            <ProgressOverviewSection scans={scans} loading={loading} data-id="element-3686" data-mp="2wnyr0nbrxkmm" />
          </motion.div>

          {/* Communication Style Widget */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.35
        }} data-id="element-3687" data-mp="2rocdsdvy44dt">
            <CommunicationStyleWidget scans={scans} data-id="element-3688" data-mp="2ds8o8xw0ok7o" />
          </motion.div>

          {/* Subscription Status - Forensic Style with 3-tier display */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className={`relative bg-[#0a0a0a] border-2 p-6 md:p-8 mb-10 ${userTier === 'pro' ? 'border-[#FFD700]/50' : userTier === 'active' ? 'border-green-500/30' : 'border-gray-700'}`} data-id="element-3689" data-mp="241nq1bk99hpa">
            {/* Corner Brackets */}
            <div className={`absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 ${userTier === 'pro' ? 'border-[#FFD700]' : userTier === 'active' ? 'border-green-500' : 'border-gray-600'}`} data-id="element-3690" data-mp="9ikpmfjtn1py" />
            <div className={`absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 ${userTier === 'pro' ? 'border-[#FFD700]' : userTier === 'active' ? 'border-green-500' : 'border-gray-600'}`} data-id="element-3691" data-mp="jg7jq99oyzui" />
            <div className={`absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 ${userTier === 'pro' ? 'border-[#FFD700]' : userTier === 'active' ? 'border-green-500' : 'border-gray-600'}`} data-id="element-3692" data-mp="3ryn70lj3amsq" />
            <div className={`absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 ${userTier === 'pro' ? 'border-[#FFD700]' : userTier === 'active' ? 'border-green-500' : 'border-gray-600'}`} data-id="element-3693" data-mp="2zahkbt8h6nq9" />

            <div className="flex items-center justify-between flex-wrap gap-4" data-id="element-3694" data-mp="19t3vqf338pg2">
              <div data-id="element-3695" data-mp="15s0hty4lp3hh">
                <div className="flex items-center gap-3 mb-2" data-id="element-3696" data-mp="15sk1pkdta1y5">
                  <p className={`text-[10px] font-mono uppercase tracking-widest ${userTier === 'pro' ? 'text-[#FFD700]' : userTier === 'active' ? 'text-green-500' : 'text-gray-500'}`} data-id="element-3697" data-mp="1jinfro2jepci">
                    Clearance Level
                  </p>
                  {/* Tier Badge */}
                  <span className={`px-2 py-0.5 border text-[10px] font-mono font-bold uppercase tracking-widest ${userTier === 'pro' ? 'bg-[#FFD700]/10 text-[#FFD700] border-[#FFD700]/30' : userTier === 'active' ? 'bg-green-500/10 text-green-500 border-green-500/30' : 'bg-gray-800 text-gray-500 border-gray-700'}`} data-id="element-3698" data-mp="1ogoxo5o88ime">
                    {userTier === 'pro' ? '★ PRO' : userTier === 'active' ? 'PAY-PER-SCAN' : 'FREE'}
                  </span>
                </div>
                <div className="flex items-baseline gap-3" data-id="element-3699" data-mp="2jpiv2w7qu4rl">
                  {loadingProStatus ? <p className="text-2xl font-black text-white uppercase tracking-tight" data-id="element-3700" data-mp="25ydzc57hxwmj">
                      Verifying...
                    </p> : userTier === 'pro' ? <p className="text-2xl font-black text-[#FFD700] uppercase tracking-tight" data-id="element-3701" data-mp="1vzybcb3rn3h9">
                      ∞ Unlimited Scans
                    </p> : <p className="text-2xl font-black text-white uppercase tracking-tight" data-id="element-3702" data-mp="1n1336th5vjbb">
                      Scan Credits:{' '}
                      <span className={scansCount > 0 ? 'text-green-500' : 'text-[#FF0000]'} data-id="element-3703" data-mp="1r2er8ukdzwhe">
                        {scansCount}
                      </span>
                    </p>}
                </div>
                {/* Tier description */}
                <p className="text-xs font-mono text-gray-500 mt-1" data-id="element-3704" data-mp="t8437wxj9ezo">
                  {userTier === 'pro' ? 'Full forensic access — all features unlocked' : userTier === 'active' ? `${scansCount} scan${scansCount !== 1 ? 's' : ''} remaining — purchase more or upgrade to Pro` : 'No scan credits — purchase a scan or upgrade to Pro'}
                </p>
              </div>
              {!isPro && !loadingProStatus && <button onClick={() => {
              trackCriticalEvent('Upgrade_CTA_Clicked', {
                cta_location: 'subscription_card',
                current_plan: userTier,
                scans_remaining: scansCount
              });
              openUpgradeModal('plans');
            }} className="inline-flex items-center gap-2 px-6 py-3 bg-[#FFD700] text-black font-bold uppercase tracking-wider hover:bg-[#FFD700]/90 transition-all text-sm shadow-[0_0_20px_rgba(255,215,0,0.4)]" data-id="element-3705" data-mp="184mfzmla870w">
                  {userTier === 'active' ? 'Upgrade to Pro' : 'Get Started'}
                  <ChevronRight size={16} data-id="element-3706" data-mp="2h5wev9i1xj4w" />
                </button>}
            </div>
          </motion.div>

          {/* Referral viral loop */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.45
        }} className="mb-10" data-id="element-3707" data-mp="2m3523oi24dd0">
            <ReferralBanner data-id="element-3708" data-mp="34w0kfmesvsyj" />
          </motion.div>

          {/* Quick Actions - Forensic Style */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="mb-10" data-id="element-3709" data-mp="39uk7jq54rash">
            <h3 className="text-lg font-black text-white mb-6 uppercase tracking-tight flex items-center gap-2" data-id="element-3710" data-mp="8xiw1s1p4uuv">
              <span className="w-1 h-4 bg-[#FF0000]" data-id="element-3711" data-mp="400jzssdtzin" />
              Command Center
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3712" data-mp="3cfslu7i58133">
              <Link to="/upload" className="group" data-id="element-3713" data-mp="37gz8cf2uwa2x">
                <div className="relative bg-[#0a0a0a] border border-gray-800 p-6 group-hover:border-[#FF0000]/50 transition-all duration-300 h-full" data-id="element-3714" data-mp="37e3bku2qvrg2">
                  <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-3715" data-mp="3he3zktbk6vql" />
                  <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-3716" data-mp="2tmoooc5pv63q" />
                  <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-3717" data-mp="147dya5bmy79c" />
                  <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-3718" data-mp="190gtsizz3qs6" />

                  <div className="flex items-center gap-4" data-id="element-3719" data-mp="z9manni0x7ay">
                    <div className="flex items-center justify-center w-12 h-12 bg-gray-800 border border-gray-700 text-gray-400 group-hover:bg-[#FF0000]/10 group-hover:border-[#FF0000] group-hover:text-[#FF0000] transition-all duration-300" data-id="element-3720" data-mp="1my5hdylfdc0x">
                      <Upload size={24} data-id="element-3721" data-mp="1dwfiy8t4lctn" />
                    </div>
                    <div data-id="element-3722" data-mp="1hyslkocksum7">
                      <h4 className="font-bold text-white mb-1 uppercase tracking-wide text-sm group-hover:text-[#FF0000] transition-colors" data-id="element-3723" data-mp="1hy11a9ofijb8">
                        Initialize Scan
                      </h4>
                      <p className="text-xs text-gray-500 font-mono" data-id="element-3724" data-mp="6koqtfy5z2g3">
                        Submit evidence for forensic analysis
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
              <Link to="/all-scans" className="group" data-id="element-3725" data-mp="2v0dlxxmd1a2l">
                <div className="relative bg-[#0a0a0a] border border-gray-800 p-6 group-hover:border-[#FF0000]/50 transition-all duration-300 h-full" data-id="element-3726" data-mp="2uzvfk85zhfgy">
                  <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-3727" data-mp="2lau1womwva65" />
                  <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-3728" data-mp="2q2v8pv1b88os" />
                  <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-3729" data-mp="2h8qo3tcrrthp" />
                  <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-3730" data-mp="22c9eb6k8amx9" />

                  <div className="flex items-center gap-4" data-id="element-3731" data-mp="1nor2zpc5uu1v">
                    <div className="flex items-center justify-center w-12 h-12 bg-gray-800 border border-gray-700 text-gray-400 group-hover:bg-[#FF0000]/10 group-hover:border-[#FF0000] group-hover:text-[#FF0000] transition-all duration-300" data-id="element-3732" data-mp="1npalb75q3kgf">
                      <BarChart3 size={24} data-id="element-3733" data-mp="1snkl5v2e798d" />
                    </div>
                    <div data-id="element-3734" data-mp="1sqggrcqrpltn">
                      <h4 className="font-bold text-white mb-1 uppercase tracking-wide text-sm group-hover:text-[#FF0000] transition-colors" data-id="element-3735" data-mp="26nd619r8mv8f">
                        Access Case Files
                      </h4>
                      <p className="text-xs text-gray-500 font-mono" data-id="element-3736" data-mp="2gdy3uuz37vqa">
                        Review forensic intelligence reports
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </motion.div>

          {/* Recent Scans - Forensic Style */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.6
        }} className="mb-10" data-id="element-3737" data-mp="2lec9k6d7utl5">
            <div className="flex items-center justify-between mb-6" data-id="element-3738" data-mp="374vjd0gv69rt">
              <h3 className="text-lg font-black text-white uppercase tracking-tight flex items-center gap-2" data-id="element-3739" data-mp="3766n50ab7hix">
                <span className="w-1 h-4 bg-[#FF0000]" data-id="element-3740" data-mp="3c61et61wsb51" />
                Recent Case Files
              </h3>
              {recentScans.length > 0 && <Link to="/all-scans" className="text-sm font-mono text-[#FF0000] hover:text-[#FF0000]/80 transition-colors uppercase tracking-wide" data-id="element-3741" data-mp="3l4da3ldpzx3t">
                  View All →
                </Link>}
            </div>

            {loading ? <div className="space-y-4" data-id="element-3742" data-mp="1vqi0yb4vqmj6">
                {[1, 2].map(i => <div key={i} className="bg-[#0a0a0a] border border-gray-800 p-6 animate-pulse" data-id="element-3743" data-mp="1o6fphu8d0mib">
                    <div className="flex items-center gap-4" data-id="element-3744" data-mp="1jdmlx9omnk7t">
                      <div className="w-12 h-12 bg-gray-800" data-id="element-3745" data-mp="1egw7srwduyp1" />
                      <div className="flex-1 space-y-2" data-id="element-3746" data-mp="19htc3y2am4tq">
                        <div className="h-4 bg-gray-800 w-1/3" data-id="element-3747" data-mp="19f6vl8xfcezd" />
                        <div className="h-3 bg-gray-900 w-1/4" data-id="element-3748" data-mp="2740dz077pj2e" />
                      </div>
                    </div>
                  </div>)}
              </div> : recentScans.length === 0 ? <div className="relative bg-[#0a0a0a] border-2 border-gray-800 p-12 text-center" data-id="element-3749" data-mp="1x516vm23hvhi">
                <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-gray-600" data-id="element-3750" data-mp="1s7j6wl622qln" />
                <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-gray-600" data-id="element-3751" data-mp="lt0fdzdujsy2" />
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-gray-600" data-id="element-3752" data-mp="grb36gew198e" />
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-gray-600" data-id="element-3753" data-mp="25kqh2rg0f6u4" />

                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-800 border border-gray-700 mb-4" data-id="element-3754" data-mp="2afcvl3he1fif">
                  <FileText className="w-8 h-8 text-gray-600" data-id="element-3755" data-mp="20n72trjbau26" />
                </div>
                <h4 className="font-bold text-white mb-2 uppercase tracking-wide" data-id="element-3756" data-mp="2odb4htcxwjqj">
                  No Cases Filed
                </h4>
                <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto font-mono" data-id="element-3757" data-mp="2tbcpwkxu8v56">
                  Your forensic case files will appear here. Begin by submitting
                  evidence for analysis.
                </p>
                <Link to="/upload" className="inline-flex items-center justify-center px-6 py-3 bg-[#FF0000] text-white font-bold uppercase tracking-wider hover:bg-[#FF0000]/90 transition-all shadow-[0_0_20px_rgba(255,0,0,0.4)]" data-id="element-3758" data-mp="2jcdewvplonf0">
                  Initialize First Scan
                </Link>
              </div> : <div className="space-y-4" data-id="element-3759" data-mp="3h6xydwg9q49t">
                {recentScans.map(scan => <ScanCard key={scan.id} scanId={scan.id} title={scan.title} timeAgo={getTimeAgo(scan.created_at)} score={scan.score || 0} riskLevel={getRiskLevelText(scan.risk_level)} messages={scan.message_count || 0} tags={getTopPatterns(scan.patterns)} status={scan.status} patterns={scan.patterns || []} summary={(scan.analysis as any)?.summary} isUnlocked={scan.is_unlocked} data-id="element-3760" data-mp="3v6gzi5anwfcm" />)}
              </div>}
          </motion.div>

          {/* Upgrade CTA - Only for non-Pro users */}
          {!isPro && !loadingProStatus && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.7
        }} className="relative bg-gradient-to-r from-[#FF0000] via-[#FF0000]/90 to-[#FF0000] border-2 border-[#FF0000] p-8 md:p-12 text-white overflow-hidden" data-id="element-3761" data-mp="ipkqvn7ig8yg">
              {/* Scanning Line */}
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-white to-transparent animate-[scan_3s_ease-in-out_infinite]" data-id="element-3762" data-mp="4s5z4btsbp3b" />

              {/* Grid Overlay */}
              <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" data-id="element-3763" data-mp="em632adbyoag" />

              <div className="relative z-10 text-center max-w-2xl mx-auto" data-id="element-3764" data-mp="3am85fw3ts2q4">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 border border-white/30 mb-4" data-id="element-3765" data-mp="3anjcq5whbslz">
                  <Sparkles className="w-6 h-6 text-white" data-id="element-3766" data-mp="30vdgul33wr1i" />
                </div>
                <h3 className="text-2xl md:text-3xl font-black mb-3 uppercase tracking-tight text-white" data-id="element-3767" data-mp="2vwtyeunq1xdw">
                  Unlock Full Forensic Access
                </h3>
                <p className="text-white/90 mb-8 text-base md:text-lg font-mono" data-id="element-3768" data-mp="2qvmmcjejqc1f">
                  Gain unlimited forensic scans, deep behavioral pattern
                  analysis, and priority processing with Pro clearance.
                </p>
                <button onClick={() => {
              trackCriticalEvent('Upgrade_CTA_Clicked', {
                cta_location: 'dashboard_banner',
                current_plan: userTier,
                scans_remaining: scansCount
              });
              openUpgradeModal('plans');
            }} className="px-8 py-4 bg-white text-[#FF0000] font-bold uppercase tracking-wider hover:bg-gray-100 transition-all shadow-lg" data-id="element-3769" data-mp="2cyrcioqh0gkb">
                  Upgrade Clearance — 30% Off
                </button>
              </div>
            </motion.div>}
        </main>
      </div>
    </div>;
};
function getTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return `${Math.floor(diffDays / 7)}w ago`;
}
function getRiskLevelText(riskLevel: 'low' | 'medium' | 'high' | null): string {
  if (!riskLevel) return 'Processing';
  return riskLevel === 'high' ? 'High Risk' : riskLevel === 'medium' ? 'Medium Risk' : 'Low Risk';
}
function getTopPatterns(patterns: any[] | null): string[] {
  if (!patterns || !Array.isArray(patterns) || patterns.length === 0) return [];
  return patterns.slice(0, 2).map(p => p.type || p);
}